import React, { useState, useEffect, useRef } from "react";
import FollowPageMatrice from "../../pages/lakayUser/followPageMatrice";
import ButtonPagination from "../../pageInside/ButtonPagination";
import { toImageUrl } from "../../../config";
import { getProductsF } from "../../auth/apiProduct";
import { readHistorySearchLS, getFirstIds } from "../../auth";
import { processData, getLastItem, getImageUrl } from "./swiperPageMatrix";
import { getPaginationLimit } from "../../../utils/paginationSettings";
import InfiniteScrollLoader from "../../pageInside/InfiniteScrollLoader";
import { mergeUniqueById, normalizeInfiniteScrollPageId, resolveInfiniteScroll } from "../../../utils/infiniteScrollSettings";
import "./MatrixCardsGrid.css";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MatrixCardsGrid — the "See All" companion to SwiperPageMatrix.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * SwiperPageMatrix renders these same cards (FollowPageMatrice) inside a
 * horizontal Swiper for the Home slider, unsliced (every group Home ever
 * gets). This component renders the exact same cards — same data shaping
 * (processData/getLastItem/getImageUrl), same FollowPageMatrice component,
 * same props — inside a responsive wrap/grid, PAGINATED (self-loading, like
 * ProductGridPage): it fetches its own page of GROUPS via
 * facet:[facet] + paginateGroups:true, a request shape Home's own request
 * never sends, so Home's slider is completely unaffected.
 *
 * IMPORTANT: each card's own internal link (View All / See More, wired in
 * FollowPageMatrice) is untouched here — it keeps opening whatever
 * destination it already opens today. This grid only changes how many
 * cards are visible, how they're paginated, and how they're laid out.
 */
const MatrixCardsGrid = ({
    facet,                  // 'historySearchMatrix' | 'userViewMatrix' | 'followShopUser' — request param
    // Response key from /productsF for this facet. Usually equals `facet`,
    // but NOT for followShopUser → response key is `followShopUserMatrix`
    // (see pagesConfig.js's followShopUserGrid). Never assume they match.
    dataKey,
    infoSource,
    arrayFacet,
    myFilters,
    loadProductsByArrival,  // only meaningful for followShopUser's unfollow icon
    page,
    sectionId,
    ...props
}) => {
    const responseKey = dataKey || facet;
    const infiniteConfig = resolveInfiniteScroll(
        infoSource?.allSettings,
        normalizeInfiniteScrollPageId(page),
        sectionId
    );

    // Page size ("Load More" step) — admin-configurable via AnimationSettings
    // → General → "Pagination & Limites" (paginationLimits.<key>).
    const FACET_LIMIT_KEY = {
        historySearchMatrix: 'searchHistoryAllCards',
        userViewMatrix: 'viewingActivityAllCards',
        followShopUser: 'shopsYouFollowAllCards',
    };
    const [limit] = useState(() => (
        getPaginationLimit(infoSource?.allSettings, FACET_LIMIT_KEY[facet], 12)
    ));
    const [skip, setSkip] = useState(0);
    const [size, setSize] = useState(0);
    const [siseProduct, setSiseProduct] = useState(0);
    const [productsrMatrix, setProductsrMatrix] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const requestVersionRef = useRef(0);
    const loadingRef = useRef(false);
    useEffect(() => () => {
        requestVersionRef.current += 1;
        loadingRef.current = false;
    }, []);

    const gridRef = useRef(null);
    const scrollToGrid = () => {
        if (gridRef.current) gridRef.current.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchGroupsPage = (toSkip) => {
        if (!facet) return Promise.resolve(null);
        const userViewed    = getFirstIds(readHistorySearchLS('userView'));
        const historySearch = readHistorySearchLS('historySearch');
        return getProductsF(toSkip, limit, 'createdAt', {
            filters: myFilters?.filters || {},
            userViewed,
            historySearch,
            facet: [facet],
            // Opt-in group-level pagination — Home's own request for this
            // same facet never sends this, so it keeps getting the full,
            // unsliced list (see product.js).
            paginateGroups: true,
        });
    };

    const applyPage = (data, toSkip, append = false, version = requestVersionRef.current) => {
        if (version !== requestVersionRef.current) return;
        if (!data || data.error) {
            setLoadError(data?.error || "Unable to load this section.");
            setLoading(false);
            loadingRef.current = false;
            return;
        }
        const incoming = data[responseKey] || [];
        setProductsrMatrix((current) => append ? mergeUniqueById(current, incoming) : incoming);
        setSize(data.size ?? 0);
        setSiseProduct(data.siseProduct ?? 0);
        setSkip(toSkip);
        setLoading(false);
        loadingRef.current = false;
    };

    useEffect(() => {
        const version = ++requestVersionRef.current;
        loadingRef.current = true;
        setLoading(true);
        setLoadError("");
        setProductsrMatrix([]);
        fetchGroupsPage(0)?.then((data) => applyPage(data, 0, false, version)).catch((error) => {
            if (version !== requestVersionRef.current) return;
            setLoadError(error?.message || "Unable to load this section.");
            setLoading(false);
            loadingRef.current = false;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facet, infiniteConfig.enabled]);

    const loadMore = () => {
        if (loadingRef.current) return;
        const append = infiniteConfig.enabled;
        const toSkip = append ? productsrMatrix.length : skip + limit;
        if (!append) scrollToGrid();
        const version = requestVersionRef.current;
        loadingRef.current = true;
        setLoading(true);
        setLoadError("");
        fetchGroupsPage(toSkip)?.then((data) => applyPage(data, toSkip, append, version)).catch((error) => {
            if (version !== requestVersionRef.current) return;
            setLoadError(error?.message || "Unable to load this section.");
            setLoading(false);
            loadingRef.current = false;
        });
    };

    const loadMore0 = () => {
        const toSkip = Math.max(0, skip - limit);
        scrollToGrid();
        fetchGroupsPage(toSkip)?.then((data) => applyPage(data, toSkip)).catch(() => {});
    };

    const items = processData(productsrMatrix);

    if ((!items || items.length === 0) && !loading && !loadError) return null;

    return (
        <div ref={gridRef}>
            {infiniteConfig.titleMode === 'custom' && infiniteConfig.customTitle ? <h2>{infiniteConfig.customTitle}</h2> : null}
            <div className="matrix-cards-grid">
                {items.map((products, index) => (
                    <div className="matrix-cards-grid-item" key={index}>
                        <FollowPageMatrice
                            products={products}
                            title={true}
                            arrayFacet={arrayFacet}
                            infoSource={infoSource}
                            facet={facet}
                            loadProductsByArrival={loadProductsByArrival}
                            headerBackgroundImage={toImageUrl(
                                getImageUrl(getLastItem(products.products))
                            )}
                            {...props}
                        />
                    </div>
                ))}
            </div>

            <InfiniteScrollLoader
                enabled={infiniteConfig.enabled}
                hasMore={items.length < siseProduct && size > 0}
                loading={loading}
                error={loadError}
                onLoadMore={loadMore}
                onRetry={loadMore}
                skeleton={<div className="matrix-cards-grid matrix-cards-grid-skeleton" aria-hidden="true">{Array.from({ length: Math.min(limit, 4) }, (_, index) => <div className="matrix-card-skeleton" key={index}><i /><b /><span /></div>)}</div>}
            />

            {!infiniteConfig.enabled && <ButtonPagination
                infoSource={infoSource}
                size={size}
                skip={skip}
                limit={limit}
                siseProduct={siseProduct}
                loadMore={loadMore}
                loadMore0={loadMore0}
            />}
        </div>
    );
};

export default MatrixCardsGrid;
